
import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useCartStore } from "@/store/cart";
import { Minus, Plus, ShoppingBag, Check } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { motion } from "framer-motion";

interface ProductConfigDialogProps {
  isOpen: boolean;
  onClose: () => void;
  product: {
    id: string;
    title: string;
    description?: string;
    price: number;
    discountedPrice?: number;
    imageUrl: string | string[];
    category: string;
    availableSizes?: string[];
    availableColors?: string[];
    printOptions?: string[];
  };
}

export const ProductConfigDialog = ({
  isOpen,
  onClose,
  product,
}: ProductConfigDialogProps) => {
  const { addItem } = useCartStore();
  const { toast } = useToast();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedSize, setSelectedSize] = useState<string>("");
  const [selectedColor, setSelectedColor] = useState<string>("");
  const [selectedPrintOption, setSelectedPrintOption] = useState<string>("");
  const [quantity, setQuantity] = useState(1);

  // Default values if not provided
  const availableSizes = product.availableSizes || ["P", "M", "G", "GG"];
  const availableColors = product.availableColors || ["Preto", "Branco", "Cinza"];
  const printOptions = product.printOptions || ["Frente", "Costas", "Ambos"];
  
  // Handle carousel navigation
  const images = Array.isArray(product.imageUrl) ? product.imageUrl : [product.imageUrl];
  
  const handlePreviousImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleAddToCart = () => {
    if (!selectedSize || !selectedColor || !selectedPrintOption) {
      toast({
        title: "Por favor, selecione todas as opções",
        description: "Selecione tamanho, cor e opção de estampa",
        variant: "destructive",
      });
      return;
    }

    addItem({
      id: product.id,
      title: product.title,
      price: product.price,
      discountedPrice: product.discountedPrice,
      imageUrl: Array.isArray(product.imageUrl) ? product.imageUrl[0] : product.imageUrl,
      quantity,
      size: selectedSize,
      color: selectedColor,
      printOption: selectedPrintOption,
    });

    toast({
      title: "Produto adicionado ao carrinho",
      description: `${product.title} foi adicionado ao seu carrinho`,
    });

    onClose();
  };

  const increment = () => setQuantity((prev) => prev + 1);
  const decrement = () => setQuantity((prev) => (prev > 1 ? prev - 1 : 1));

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">{product.title}</DialogTitle>
          <DialogDescription>{product.description}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
          {/* Product Image Carousel */}
          <div className="relative aspect-square bg-muted rounded-lg overflow-hidden">
            <img
              src={images[currentImageIndex]}
              alt={`${product.title} - Imagem ${currentImageIndex + 1}`}
              className="object-cover w-full h-full"
            />
            
            {images.length > 1 && (
              <>
                <Button 
                  size="icon" 
                  variant="secondary" 
                  className="absolute left-2 top-1/2 -translate-y-1/2 z-10 opacity-70 hover:opacity-100"
                  onClick={handlePreviousImage}
                >
                  &lt;
                </Button>
                <Button 
                  size="icon" 
                  variant="secondary" 
                  className="absolute right-2 top-1/2 -translate-y-1/2 z-10 opacity-70 hover:opacity-100"
                  onClick={handleNextImage}
                >
                  &gt;
                </Button>

                {/* Pagination dots */}
                <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1">
                  {images.map((_, idx) => (
                    <button
                      key={idx}
                      className={`w-2 h-2 rounded-full ${
                        idx === currentImageIndex ? "bg-primary" : "bg-gray-300"
                      }`}
                      onClick={() => setCurrentImageIndex(idx)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Configuration Options */}
          <div className="space-y-6">
            {/* Price */}
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-2">Preço</h3>
              {product.discountedPrice ? (
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold">
                    R$ {product.discountedPrice.toFixed(2)}
                  </span>
                  <span className="text-muted-foreground line-through">
                    R$ {product.price.toFixed(2)}
                  </span>
                </div>
              ) : (
                <span className="text-2xl font-bold">
                  R$ {product.price.toFixed(2)}
                </span>
              )}
            </div>

            {/* Size Selection */}
            <div>
              <h3 className="text-lg font-semibold mb-2">Tamanho</h3>
              <div className="flex flex-wrap gap-2">
                {availableSizes.map((size) => (
                  <Button
                    key={size}
                    type="button"
                    variant={selectedSize === size ? "default" : "outline"}
                    className={`rounded-md w-12 h-12 ${
                      selectedSize === size ? "ring-2 ring-primary" : ""
                    }`}
                    onClick={() => setSelectedSize(size)}
                  >
                    {size}
                  </Button>
                ))}
              </div>
            </div>

            {/* Color Selection */}
            <div>
              <h3 className="text-lg font-semibold mb-2">Cor</h3>
              <div className="flex flex-wrap gap-2">
                {availableColors.map((color) => (
                  <Button
                    key={color}
                    type="button"
                    variant={selectedColor === color ? "default" : "outline"}
                    className={`rounded-md px-4 ${
                      selectedColor === color ? "ring-2 ring-primary" : ""
                    }`}
                    onClick={() => setSelectedColor(color)}
                  >
                    {color}
                  </Button>
                ))}
              </div>
            </div>

            {/* Print Options */}
            <div className="space-y-2">
              <h3 className="text-lg font-semibold mb-2">Estampa</h3>
              <RadioGroup
                value={selectedPrintOption}
                onValueChange={setSelectedPrintOption}
              >
                {printOptions.map((option) => (
                  <div className="flex items-center space-x-2" key={option}>
                    <RadioGroupItem value={option} id={`print-${option}`} />
                    <Label htmlFor={`print-${option}`}>{option}</Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            {/* Quantity Selection */}
            <div>
              <h3 className="text-lg font-semibold mb-2">Quantidade</h3>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={decrement}
                  disabled={quantity <= 1}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="w-8 text-center">{quantity}</span>
                <Button variant="outline" size="icon" onClick={increment}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="mr-2">
            Cancelar
          </Button>
          <Button onClick={handleAddToCart} className="gap-2">
            <ShoppingBag className="h-5 w-5" />
            Adicionar ao carrinho
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
